import React, { useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Compass, ExternalLink, Loader2, PenLine } from 'lucide-react';
import { toast } from 'sonner';

const CHOICES = ['Yes', 'Maybe', 'No'];

export const reflectionKey = (type, id) => `${type}:${id}`;

/** Parent and child fill this in together — the answers are stored as the child gave them. */
export function ReflectionDialog({ childId, experience, existing, questions, onSaved, onClose }) {
  const [answers, setAnswers] = useState({
    learned: existing?.answers?.learned || '',
    surprised: existing?.answers?.surprised || '',
    difficult: existing?.answers?.difficult || '',
    explore_further: existing?.answers?.explore_further || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setAnswers((a) => ({ ...a, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put(
        `/children/${childId}/reflections/${experience.type}/${experience.id}`, answers);
      toast.success(res.data.edited ? 'Reflection updated' : 'Reflection saved');
      if (res.data.discovery?.status === 'pending') {
        toast.info('Your answers are saved. My Career Discovery will refresh shortly.');
      }
      onSaved(res.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save this reflection');
    }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-2xl sm:max-w-lg max-h-[90vh] overflow-y-auto"
                     data-testid="reflection-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Outfit']">
            {existing ? 'Edit reflection' : 'Add reflection'} — {experience.careerName}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {experience.label}{experience.date ? ` · ${experience.date}` : ''}. Fill this in together
            and write what your child says in their own words. It is optional and changes nothing
            about feedback or certificates.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-1">
          {(questions || []).filter((q) => !q.choices).map((q) => (
            <div key={q.key}>
              <label className="text-xs font-semibold text-[#1F2937]" htmlFor={`reflection-${q.key}`}>
                {q.question}
              </label>
              <Textarea
                id={`reflection-${q.key}`}
                value={answers[q.key]}
                onChange={(e) => set(q.key, e.target.value)}
                rows={3}
                maxLength={600}
                placeholder="In your child's own words"
                className="mt-1.5 rounded-xl text-sm"
                data-testid={`reflection-input-${q.key}`}
              />
            </div>
          ))}

          <div>
            <p className="text-xs font-semibold text-[#1F2937]">
              Would you like to explore this career further?
            </p>
            <div className="flex gap-2 mt-2">
              {CHOICES.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => set('explore_further', answers.explore_further === c ? '' : c)}
                  className={`px-4 h-9 rounded-xl text-xs font-semibold border transition-colors ${
                    answers.explore_further === c
                      ? 'bg-[#F97D6A] text-white border-[#F97D6A]'
                      : 'bg-white text-[#4B5563] border-gray-200 hover:bg-gray-50'}`}
                  data-testid={`reflection-explore-${c.toLowerCase()}`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter className="pt-2">
          <Button variant="outline" onClick={onClose} className="rounded-xl"
                  data-testid="reflection-cancel">
            Cancel
          </Button>
          <Button onClick={save} disabled={saving}
                  className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl"
                  data-testid="reflection-save">
            {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}
            {existing ? 'Save changes' : 'Save reflection'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ReflectionAction({ existing, onOpen, testId }) {
  return (
    <Button size="sm" variant="outline" onClick={onOpen}
            className="rounded-lg h-8 text-xs px-3 border-[#F97D6A]/40 text-[#F97D6A] hover:bg-[#FFF0ED]"
            data-testid={testId}>
      <PenLine className="h-3.5 w-3.5 mr-1.5" />
      {existing ? 'View / edit reflection' : 'Add reflection'}
    </Button>
  );
}

/** What the reflections, taken together, seem to be saying. Observations stay separate from guesses.
 *  Regeneration is automatic on every reflection save — there is no manual AI trigger here. */
export function CareerDiscoverySection({ discovery, reflectionCount }) {
  const when = discovery?.generated_at
    ? new Date(discovery.generated_at).toLocaleString('en-GB',
      { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : null;

  return (
    <div className="ce-card p-4" data-testid="career-discovery-section">
      <div className="flex items-start justify-between gap-3 mb-3">
        <h3 className="text-sm font-bold text-[#1F2937] font-['Outfit'] flex items-center gap-2">
          <Compass className="h-4 w-4 text-[#F97D6A]" /> My Career Discovery
        </h3>
      </div>

      {!discovery && (
        <p className="text-xs text-[#9CA3AF]" data-testid="discovery-empty">
          Add a reflection after a completed experience and a short summary of what your child is
          discovering appears here.
        </p>
      )}

      {discovery?.status === 'pending' && !discovery.discovering && (
        <p className="text-xs text-[#6B7280]" data-testid="discovery-pending">
          Your reflections are saved. The summary could not be written just now — it will be written
          the next time a reflection is added or edited.
        </p>
      )}

      {discovery?.discovering && (
        <div className="space-y-4">
          {discovery.status === 'pending' && (
            <p className="text-[11px] text-amber-700 bg-amber-50 rounded-lg px-3 py-2"
               data-testid="discovery-stale-note">
              This is the previous summary — the latest update did not go through. Your reflections
              are safe, and the summary will catch up the next time one is added or edited.
            </p>
          )}
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-[#9CA3AF] mb-1">
              What I'm Discovering
            </p>
            <p className="text-sm text-[#4B5563] leading-relaxed" data-testid="discovery-text">
              {discovery.discovering}
            </p>
          </div>

          {discovery.interests?.length > 0 && (
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-[#9CA3AF] mb-1.5">
                Emerging Interests
              </p>
              <div className="flex flex-wrap gap-1.5" data-testid="discovery-interests">
                {discovery.interests.map((i) => (
                  <span key={i} className="text-[11px] font-medium bg-[#F5F6F8] text-[#4B5563] px-2 py-0.5 rounded-lg border border-gray-100">
                    {i}
                  </span>
                ))}
              </div>
            </div>
          )}

          {discovery.careers?.length > 0 && (
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-[#9CA3AF] mb-1.5">
                Careers Worth Exploring Next
              </p>
              <div className="space-y-1.5" data-testid="discovery-careers">
                {discovery.careers.map((c) => (
                  <div key={c.name} className="rounded-xl bg-[#F8F9FA] px-3 py-2"
                       data-testid={`discovery-career-${(c.slug || c.name).toLowerCase()}`}>
                    <p className="text-xs font-semibold text-[#1F2937] flex items-center gap-1.5">
                      {c.slug ? (
                        <a href={`/careers/${c.slug}`} target="_blank" rel="noreferrer"
                           className="text-[#F97D6A] hover:underline inline-flex items-center gap-1">
                          {c.name} <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : c.name}
                    </p>
                    {c.why && <p className="text-[11px] text-[#6B7280] mt-0.5 leading-relaxed">{c.why}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          <p className="text-[11px] text-[#9CA3AF] pt-1 border-t border-gray-100"
             data-testid="discovery-meta">
            Based on {discovery.reflection_count || reflectionCount} reflection
            {(discovery.reflection_count || reflectionCount) === 1 ? '' : 's'}
            {when ? ` · last updated ${when}` : ''}. These are observations from your child's own
            answers, not an aptitude test or a career decision.
          </p>
        </div>
      )}
    </div>
  );
}
